import React, { useRef, useEffect, useState, useCallback, useMemo } from 'react';
import type { WheelSegment } from './types';
import { resolveBrandFromSurveyObject, type ScratchCardBrand } from './ScratchCardCanvas';

/**
 * Golazo — Xotski penalty-shootout game (Gate 6).
 *
 * Design law (game-engine.md §2): the brand is structural, not decorative.
 * The brand lives on the GOAL, NET, KEEPER, and PITCH — the ball is a neutral,
 * authentic black-and-white football by design (its authenticity is the cultural
 * value; a logo ball would read fake). See docs/prds/apps/xotski/golazo-game.md §8.
 *
 * Which prize is NOT decided here. The wrapper picks the winner with the shared
 * prize engine (calculateWinningSegment, (6-rating)²) and passes it as `prize`.
 * This component choreographs the shot to home into that prize's pocket and
 * fires onRevealed. The guest ALWAYS wins — the keeper dives but is always
 * beaten. There is no keeper-save / Try-Again / no-win path (locked protocol).
 *
 * Ported from the working prototype: _reference/golazo-prototype.html.
 */

export type SoccerPhase = 'ready' | 'aiming' | 'flight' | 'scored' | 'winReveal';
/** Brand placement behind the goal (stadium sponsor surface). */
export type SoccerBackdrop = 'jumbotron' | 'billboard' | 'both' | 'none';

export interface SoccerBallProps {
  /** The prize the engine already chose. The guest always wins something. */
  prize: WheelSegment;
  /** Resolved 5-color brand. Use resolveBrandFromSurveyObject() for the 3-color shape. */
  brand: ScratchCardBrand;
  /** Up to 5 prizes shown in the goal pockets. The winner is forced into one. */
  pocketPrizes?: WheelSegment[];
  /** Brand logo placement behind the goal — stadium jumbotron / LED billboard. */
  backdrop?: SoccerBackdrop;
  language?: 'en' | 'es';
  reducedMotion?: boolean;
  /** Force a visual phase for Storybook state stories (skips interaction). */
  demoPhase?: SoccerPhase;
  /** Fires once when the goal resolves, with the won prize. */
  onRevealed?: (prize: WheelSegment) => void;
}

// Re-export so the registry can import the resolver from one place.
export { resolveBrandFromSurveyObject };
export type { ScratchCardBrand };

// ── Scene geometry (from the prototype) ──
const CW = 350, CH = 486, CX = CW / 2;
const SPOT = { x: CX, y: 430 };
const GOAL_Y = 92;
const GW = 184, GH = 96;
const AMP = 48;
const MAXPULL = 150;
const FLIGHT_FRAMES = 74;

interface Pocket { key: string; dx: number; dy: number; w: number; h: number; }
const POCKETS: Pocket[] = [
  { key: 'TL', dx: -60, dy: 34, w: 48, h: 34 },
  { key: 'TC', dx: 0, dy: 30, w: 50, h: 34 },
  { key: 'TR', dx: 60, dy: 34, w: 48, h: 34 },
  { key: 'BL', dx: -44, dy: 72, w: 46, h: 30 },
  { key: 'BR', dx: 44, dy: 72, w: 46, h: 30 },
];

const T = {
  hint: { en: 'Shoot to score', es: 'Tira para anotar' },
  sub: { en: 'Drag back from the ball and release', es: 'Arrastra hacia atrás y suelta' },
  shoot: { en: 'Shoot', es: 'Tirar' },
  scored: { en: 'GOAL', es: '¡GOOOL!' },
  power: { en: 'POWER', es: 'FUERZA' },
} as const;

// ── Color helpers ──
function hexRgb(hex: string): [number, number, number] {
  let h = hex.replace('#', '');
  if (h.length === 3) h = h.split('').map((c) => c + c).join('');
  return [parseInt(h.slice(0, 2), 16), parseInt(h.slice(2, 4), 16), parseInt(h.slice(4, 6), 16)];
}
function clampByte(c: number) { return Math.max(0, Math.min(255, Math.round(c))); }
function rgbHex(r: number, g: number, b: number) {
  return '#' + [r, g, b].map((c) => clampByte(c).toString(16).padStart(2, '0')).join('');
}
function darken(hex: string, factor = 0.55) {
  const [r, g, b] = hexRgb(hex);
  return rgbHex(r * factor, g * factor, b * factor);
}
function lighten(hex: string, amt = 0.35) {
  const [r, g, b] = hexRgb(hex);
  return rgbHex(r + (255 - r) * amt, g + (255 - g) * amt, b + (255 - b) * amt);
}
function toRgba(hex: string, a: number) {
  const [r, g, b] = hexRgb(hex);
  return `rgba(${r},${g},${b},${a})`;
}
const lerp = (a: number, b: number, t: number) => a + (b - a) * t;
const clamp = (v: number, a: number, b: number) => Math.max(a, Math.min(b, v));
const isLightBg = (font: string) => font === '#000' || font === '#000000';

function roundRect(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, r: number) {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y, x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x, y + h, r);
  ctx.arcTo(x, y + h, x, y, r);
  ctx.arcTo(x, y, x + w, y, r);
  ctx.closePath();
}

// ── Neutral ball geometry: truncated icosahedron with TRUE projected panels ──
interface Panel { center: number[]; corners: number[][]; }
function buildBall(): { pentagons: Panel[]; hexagons: Panel[] } {
  const phi = (1 + Math.sqrt(5)) / 2;
  const icoRaw: number[][] = [];
  for (const s1 of [1, -1]) for (const s2 of [1, -1]) {
    [[0, s1, s2 * phi], [s1, s2 * phi, 0], [s2 * phi, 0, s1]].forEach((v) => icoRaw.push(v));
  }
  const norm3 = ([x, y, z]: number[]): number[] => { const l = Math.sqrt(x * x + y * y + z * z); return [x / l, y / l, z / l]; };
  const ICO = icoRaw.map(norm3);
  const d2 = ([x1, y1, z1]: number[], [x2, y2, z2]: number[]) => (x1 - x2) ** 2 + (y1 - y2) ** 2 + (z1 - z2) ** 2;
  const add = (a: number[], b: number[]) => [a[0] + b[0], a[1] + b[1], a[2] + b[2]];
  const cross = (a: number[], b: number[]) => [a[1] * b[2] - a[2] * b[1], a[2] * b[0] - a[0] * b[2], a[0] * b[1] - a[1] * b[0]];
  const dot = (a: number[], b: number[]) => a[0] * b[0] + a[1] * b[1] + a[2] * b[2];
  const trunc = (from: number[], to: number[]) => norm3([from[0] + (to[0] - from[0]) / 3, from[1] + (to[1] - from[1]) / 3, from[2] + (to[2] - from[2]) / 3]);
  const order = (center: number[], corners: number[][]) => {
    const c = norm3(center);
    const ref = Math.abs(c[1]) < 0.9 ? [0, 1, 0] : [1, 0, 0];
    const t1 = norm3(cross(c, ref)), t2 = cross(c, t1);
    return corners.slice().sort((p, q) => Math.atan2(dot(p, t2), dot(p, t1)) - Math.atan2(dot(q, t2), dot(q, t1)));
  };
  const EDGE = 1.14, faces: number[][] = [];
  for (let i = 0; i < 12; i++) for (let j = i + 1; j < 12; j++) {
    if (d2(ICO[i], ICO[j]) > EDGE) continue;
    for (let k = j + 1; k < 12; k++) {
      if (d2(ICO[i], ICO[k]) < EDGE && d2(ICO[j], ICO[k]) < EDGE) faces.push([i, j, k]);
    }
  }
  const pentagons: Panel[] = ICO.map((V) => {
    const corners = ICO.filter((W) => W !== V && d2(V, W) < EDGE).map((W) => trunc(V, W));
    return { center: V, corners: order(V, corners) };
  });
  const hexagons: Panel[] = faces.map(([i, j, k]) => {
    const a = ICO[i], b = ICO[j], c = ICO[k];
    const corners = [trunc(a, b), trunc(b, a), trunc(b, c), trunc(c, b), trunc(c, a), trunc(a, c)];
    const center = norm3(add(add(a, b), c));
    return { center, corners: order(center, corners) };
  });
  return { pentagons, hexagons };
}
const BALL = buildBall();

function rotateVec([x, y, z]: number[], rx: number, ry: number): number[] {
  const cx = Math.cos(rx), sx = Math.sin(rx);
  const y1 = y * cx - z * sx, z1 = y * sx + z * cx;
  const cy = Math.cos(ry), sy = Math.sin(ry);
  return [x * cy + z1 * sy, y1, -x * sy + z1 * cy];
}

// Classic black-and-white football. Neutral by design (never branded).
function drawBall(ctx: CanvasRenderingContext2D, cx: number, cy: number, R: number, rx: number, ry: number) {
  ctx.save();
  ctx.beginPath();
  ctx.ellipse(cx, cy + R * 0.96, R * 0.92, R * 0.26, 0, 0, Math.PI * 2);
  ctx.fillStyle = 'rgba(0,0,0,.32)'; ctx.fill();
  ctx.restore();

  const sph = ctx.createRadialGradient(cx - R * 0.32, cy - R * 0.36, R * 0.05, cx, cy, R);
  sph.addColorStop(0, '#FFFFFF'); sph.addColorStop(0.65, '#F4F1F9'); sph.addColorStop(1, '#D8D2E4');
  ctx.beginPath(); ctx.arc(cx, cy, R, 0, Math.PI * 2); ctx.fillStyle = sph; ctx.fill();

  ctx.save();
  ctx.beginPath(); ctx.arc(cx, cy, R, 0, Math.PI * 2); ctx.clip();

  const panels: { panel: Panel; type: string; z: number }[] = [];
  const consider = (panel: Panel, type: string) => {
    const rc = rotateVec(panel.center, rx, ry);
    if (rc[2] < -0.08) return;
    panels.push({ panel, type, z: rc[2] });
  };
  BALL.hexagons.forEach((p) => consider(p, 'h'));
  BALL.pentagons.forEach((p) => consider(p, 'p'));
  panels.sort((a, b) => a.z - b.z);

  panels.forEach(({ panel, type }) => {
    ctx.beginPath();
    panel.corners.forEach((v, i) => {
      const r = rotateVec(v, rx, ry);
      const sx = cx + r[0] * R, sy = cy - r[1] * R;
      i ? ctx.lineTo(sx, sy) : ctx.moveTo(sx, sy);
    });
    ctx.closePath();
    ctx.fillStyle = type === 'h' ? '#FAFAFC' : '#15131C';
    ctx.fill();
    ctx.strokeStyle = 'rgba(28,24,38,.55)'; ctx.lineWidth = Math.max(0.7, R * 0.022);
    ctx.lineJoin = 'round'; ctx.stroke();
  });

  const shade = ctx.createRadialGradient(cx - R * 0.3, cy - R * 0.34, R * 0.1, cx, cy, R);
  shade.addColorStop(0, 'rgba(255,255,255,0)');
  shade.addColorStop(0.55, 'rgba(40,30,60,0)');
  shade.addColorStop(0.82, 'rgba(30,22,48,.20)');
  shade.addColorStop(1, 'rgba(18,12,32,.55)');
  ctx.beginPath(); ctx.arc(cx, cy, R, 0, Math.PI * 2); ctx.fillStyle = shade; ctx.fill();

  const spec = ctx.createRadialGradient(cx - R * 0.34, cy - R * 0.4, 0, cx - R * 0.2, cy - R * 0.24, R * 0.9);
  spec.addColorStop(0, 'rgba(255,255,255,.55)'); spec.addColorStop(0.3, 'rgba(255,255,255,.1)'); spec.addColorStop(1, 'transparent');
  ctx.beginPath(); ctx.arc(cx, cy, R, 0, Math.PI * 2); ctx.fillStyle = spec; ctx.fill();

  ctx.restore();
  ctx.beginPath(); ctx.arc(cx, cy, R, 0, Math.PI * 2);
  ctx.strokeStyle = 'rgba(40,30,60,.35)'; ctx.lineWidth = Math.max(0.6, R * 0.02); ctx.stroke();
}

// Build the 5 pocket prizes, guaranteeing the winner occupies a pocket.
function buildPocketSegments(winner: WheelSegment, pool?: WheelSegment[]): WheelSegment[] {
  const list: WheelSegment[] = (pool && pool.length ? pool.slice(0, 5) : [winner]).slice();
  if (!list.some((p) => String(p.id) === String(winner.id))) {
    if (list.length < 5) list.push(winner); else list[1] = winner;
  }
  let i = 0;
  while (list.length < 5) { list.push(list[i % list.length] || winner); i++; }
  return list.slice(0, 5);
}

export const SoccerBall: React.FC<SoccerBallProps> = ({
  prize,
  brand,
  pocketPrizes,
  backdrop = 'jumbotron',
  language = 'en',
  reducedMotion = false,
  demoPhase,
  onRevealed,
}) => {
  const cvRef = useRef<HTMLCanvasElement>(null);
  const phaseRef = useRef<SoccerPhase>(demoPhase || 'ready');
  const goalPhase = useRef(0);
  const goalSlow = useRef(1);
  const ballRot = useRef({ x: 0.3, y: 0 });
  const ballPos = useRef({ x: SPOT.x, y: SPOT.y, r: 30 });
  const trail = useRef<{ x: number; y: number; r: number }[]>([]);
  const flight = useRef<{ t: number; S: { x: number; y: number }; C: { x: number; y: number } } | null>(null);
  const keeper = useRef({ dive: 0, dir: 0 });
  const fx = useRef({ ripple: 0, pocket: -1, flash: 0, shake: 0 });
  const drag = useRef({ active: false, sx: 0, sy: 0, cx: 0, cy: 0 });
  const revealedFired = useRef(false);
  const logoImg = useRef<HTMLImageElement | null>(null);
  const [, force] = useState(0);
  const [hint, setHint] = useState(true);

  // Preload the brand logo for the stadium backdrop (jumbotron / billboard).
  useEffect(() => {
    if (!brand.logo) { logoImg.current = null; return; }
    const im = new Image();
    im.crossOrigin = 'anonymous';
    im.onload = () => { logoImg.current = im; force((n) => n + 1); };
    im.src = brand.logo;
    return () => { im.onload = null; };
  }, [brand.logo]);

  const pockets = useMemo(() => buildPocketSegments(prize, pocketPrizes), [prize, pocketPrizes]);
  const targetIdx = useMemo(() => {
    const i = pockets.findIndex((p) => String(p.id) === String(prize.id));
    return i < 0 ? 1 : i;
  }, [pockets, prize]);

  const lightBg = isLightBg(brand.font);
  const fgMuted = toRgba(brand.font, 0.58);
  const fgSubtle = toRgba(brand.font, 0.3);

  // Brand-derived scene palette (zero hardcoded brand hex). Pitch/sky from a
  // darkened primary so white regulation markings read on light AND dark brands.
  const P = useMemo(() => ({
    skyTop: darken(brand.primary, 0.42),
    skyMid: darken(brand.primary, 0.3),
    skyBot: darken(brand.primary, 0.16),
    pitchTop: darken(brand.primary, 0.26),
    pitchBot: darken(brand.primary, 0.36),
    glow: brand.secondary,
    accent: brand.accent,
    jersey: brand.primary,
    gloves: brand.accent,
    netBack: toRgba(brand.secondary, 0.07),
    netLine: toRgba(lighten(brand.secondary, 0.5), 0.16),
  }), [brand]);

  const goalX = useCallback(() => CX + Math.sin(goalPhase.current) * AMP, []);
  const pocketPos = useCallback((idx: number, gx?: number) => {
    const p = POCKETS[idx];
    return { x: (gx ?? goalX()) + p.dx, y: GOAL_Y + p.dy };
  }, [goalX]);

  const aimVector = useCallback(() => {
    const d = drag.current;
    const dx = d.cx - d.sx, dy = d.cy - d.sy;
    const len = Math.sqrt(dx * dx + dy * dy);
    if (len < 8) return null;
    return { power: clamp(len / MAXPULL, 0.12, 1), fx: -dx / len, fy: -dy / len };
  }, []);

  // The shot: prize already chosen, choreograph the trajectory to its pocket.
  const launch = useCallback((override?: { power: number; fx: number; fy: number }) => {
    if (phaseRef.current !== 'ready') return;
    const fwd = override || aimVector();
    const power = fwd ? fwd.power : 0.6;
    const fdx = fwd ? fwd.fx : 0, fdy = fwd ? fwd.fy : -1;
    const gx = goalX();
    const end = pocketPos(targetIdx, gx);
    const ctrlDist = lerp(150, 250, power);
    const C = { x: SPOT.x + fdx * ctrlDist, y: SPOT.y + fdy * ctrlDist - power * 70 };
    flight.current = { t: 0, S: { x: SPOT.x, y: SPOT.y }, C };
    // Always-win: the keeper commits the WRONG way (beaten).
    const pocketSide = end.x > gx ? 1 : (end.x < gx ? -1 : 1);
    keeper.current.dir = -pocketSide;
    goalSlow.current = 0.32;
    setHint(false);
    if (reducedMotion) {
      // Degrade to an instant score (no long flight).
      phaseRef.current = 'scored';
      ballPos.current = { x: end.x, y: end.y, r: 11 };
      fx.current = { ripple: 1, pocket: targetIdx, flash: 1, shake: 0 };
      trail.current = [];
      if (!revealedFired.current) {
        revealedFired.current = true;
        phaseRef.current = 'winReveal';
        onRevealed?.(prize);
      }
    } else {
      phaseRef.current = 'flight';
    }
  }, [aimVector, goalX, pocketPos, targetIdx, reducedMotion, onRevealed, prize]);

  // ── Drawing ──
  const draw = useCallback(() => {
    const cv = cvRef.current; if (!cv) return;
    const ctx = cv.getContext('2d'); if (!ctx) return;
    const phase = phaseRef.current;
    const shake = fx.current.shake;
    const ox = shake ? (Math.random() - 0.5) * shake : 0, oy = shake ? (Math.random() - 0.5) * shake : 0;
    ctx.save(); ctx.translate(ox, oy);
    ctx.clearRect(-8, -8, CW + 16, CH + 16);

    // sky / stadium
    const sky = ctx.createLinearGradient(0, 0, 0, CH);
    sky.addColorStop(0, P.skyTop); sky.addColorStop(0.42, P.skyMid); sky.addColorStop(1, P.skyBot);
    ctx.fillStyle = sky; ctx.fillRect(0, 0, CW, CH);

    [[40, 18], [CW - 40, 18], [CX, 6]].forEach(([lx, ly]) => {
      const g = ctx.createRadialGradient(lx, ly, 0, lx, ly, 150);
      g.addColorStop(0, toRgba(lighten(P.accent, 0.3), 0.16)); g.addColorStop(1, 'transparent');
      ctx.fillStyle = g; ctx.fillRect(0, 0, CW, CH);
    });

    // ── BRAND BACKDROP: stadium sponsor surface behind the goal ──
    if (backdrop !== 'none') drawBackdrop(ctx);

    // ── PITCH: gentle-perspective field ──
    const pitchTop = GOAL_Y + GH + 4;
    const yAt = (t: number) => lerp(pitchTop, CH, t);
    const halfAt = (t: number) => lerp(150, 250, t);
    const px = (frac: number, t: number) => CX + frac * halfAt(t);
    ctx.beginPath();
    ctx.moveTo(px(-1, 0), yAt(0)); ctx.lineTo(px(1, 0), yAt(0));
    ctx.lineTo(px(1, 1), yAt(1)); ctx.lineTo(px(-1, 1), yAt(1)); ctx.closePath();
    const pg = ctx.createLinearGradient(0, pitchTop, 0, CH);
    pg.addColorStop(0, P.pitchTop); pg.addColorStop(1, P.pitchBot);
    ctx.fillStyle = pg; ctx.fill();
    // mow lanes
    ctx.save(); ctx.clip();
    const LANES = 7;
    for (let c = 0; c < LANES; c++) {
      if (c % 2) continue;
      const fa0 = -1 + 2 * c / LANES, fa1 = -1 + 2 * (c + 1) / LANES;
      ctx.beginPath();
      ctx.moveTo(px(fa0, 0), yAt(0)); ctx.lineTo(px(fa1, 0), yAt(0));
      ctx.lineTo(px(fa1, 1), yAt(1)); ctx.lineTo(px(fa0, 1), yAt(1)); ctx.closePath();
      ctx.fillStyle = 'rgba(255,255,255,.022)'; ctx.fill();
    }
    ctx.restore();

    // pitch markings (white = neutral regulation, like the ball)
    ctx.strokeStyle = 'rgba(245,243,255,.42)'; ctx.lineWidth = 2; ctx.lineJoin = 'round';
    ctx.beginPath(); ctx.moveTo(px(-1, 0.01), yAt(0.01)); ctx.lineTo(px(1, 0.01), yAt(0.01)); ctx.stroke();
    const pb0 = 0.02, pb1 = 0.6, pbf = 0.78;
    ctx.beginPath();
    ctx.moveTo(px(-pbf, pb0), yAt(pb0)); ctx.lineTo(px(-pbf, pb1), yAt(pb1));
    ctx.lineTo(px(pbf, pb1), yAt(pb1)); ctx.lineTo(px(pbf, pb0), yAt(pb0)); ctx.stroke();
    const sb1 = 0.26, sbf = 0.42;
    ctx.beginPath();
    ctx.moveTo(px(-sbf, pb0), yAt(pb0)); ctx.lineTo(px(-sbf, sb1), yAt(sb1));
    ctx.lineTo(px(sbf, sb1), yAt(sb1)); ctx.lineTo(px(sbf, pb0), yAt(pb0)); ctx.stroke();
    ctx.beginPath();
    ctx.ellipse(CX, yAt(pb1), halfAt(pb1) * 0.3, 16, 0, 0.12 * Math.PI, 0.88 * Math.PI); ctx.stroke();
    ctx.beginPath(); ctx.arc(SPOT.x, SPOT.y + 2, 3, 0, Math.PI * 2); ctx.fillStyle = 'rgba(245,243,255,.7)'; ctx.fill();

    const gx = goalX();

    // GOAL: net + pockets, keeper, then frame in front
    drawGoalNet(ctx, gx);
    drawPockets(ctx, gx);
    drawKeeper(ctx, gx);
    drawGoalFrame(ctx, gx);

    // score fx (ripple at pocket)
    if (fx.current.ripple > 0) {
      const pp = pocketPos(fx.current.pocket, gx);
      const rr = (1 - fx.current.ripple) * 52;
      ctx.beginPath(); ctx.arc(pp.x, pp.y, rr, 0, Math.PI * 2);
      ctx.strokeStyle = toRgba(P.accent, fx.current.ripple * 0.9); ctx.lineWidth = 3; ctx.stroke();
      ctx.beginPath(); ctx.arc(pp.x, pp.y, rr * 0.6, 0, Math.PI * 2);
      ctx.strokeStyle = `rgba(255,255,255,${fx.current.ripple * 0.6})`; ctx.lineWidth = 1.5; ctx.stroke();
    }

    // trail
    trail.current.forEach((p, i) => {
      const a = (i / trail.current.length) * 0.4;
      ctx.beginPath(); ctx.arc(p.x, p.y, p.r * 0.7, 0, Math.PI * 2);
      ctx.fillStyle = toRgba(P.accent, a); ctx.fill();
    });

    // ball
    const bp = ballPos.current;
    drawBall(ctx, bp.x, bp.y, bp.r, ballRot.current.x, ballRot.current.y);

    // aim guide + power meter
    const aimView = drag.current.active ? aimVector()
      : (demoPhase === 'aiming' ? { power: 0.62, fx: 0.16, fy: -0.99 } : null);
    if (aimView) {
      ctx.save();
      ctx.setLineDash([5, 6]); ctx.lineWidth = 2.4; ctx.strokeStyle = toRgba(P.accent, 0.85);
      ctx.beginPath(); ctx.moveTo(SPOT.x, SPOT.y);
      const len = aimView.power * 150;
      const ex = SPOT.x + aimView.fx * len, ey = SPOT.y + aimView.fy * len - aimView.power * 40;
      const cxp = SPOT.x + aimView.fx * len * 0.5, cyp = SPOT.y + aimView.fy * len * 0.5 - aimView.power * 70;
      ctx.quadraticCurveTo(cxp, cyp, ex, ey); ctx.stroke();
      ctx.setLineDash([]);
      ctx.beginPath(); ctx.arc(ex, ey, 4, 0, Math.PI * 2); ctx.fillStyle = toRgba(P.accent, 0.95); ctx.fill();
      ctx.restore();
      const mh = 150, mx = 18, my = CH / 2 - mh / 2;
      roundRect(ctx, mx, my, 9, mh, 5); ctx.fillStyle = 'rgba(245,243,255,.1)'; ctx.fill();
      roundRect(ctx, mx, my + mh * (1 - aimView.power), 9, mh * aimView.power, 5);
      ctx.fillStyle = aimView.power > 0.8 ? '#F87171' : P.accent; ctx.fill();
      ctx.font = "bold 9px 'DM Sans', sans-serif"; ctx.fillStyle = fgMuted; ctx.textAlign = 'center';
      ctx.fillText(T.power[language], mx + 4.5, my - 8);
    }

    // hint
    if (hint && phase === 'ready' && !drag.current.active && !demoPhase) {
      ctx.font = "bold 14px 'DM Sans', sans-serif"; ctx.textAlign = 'center'; ctx.fillStyle = toRgba(lighten(P.accent, 0.35), 0.85);
      ctx.fillText(T.hint[language], CX, CH - 26);
      ctx.font = "10px 'DM Sans', sans-serif"; ctx.fillStyle = fgSubtle;
      ctx.fillText(T.sub[language], CX, CH - 11);
    }
    ctx.restore();

    // Fit the brand logo inside a rect, preserving aspect ratio, centered.
    function drawLogoFitted(ctx: CanvasRenderingContext2D, cx: number, cy: number, maxW: number, maxH: number) {
      const im = logoImg.current;
      if (im && im.complete && im.naturalWidth) {
        const ratio = Math.min(maxW / im.naturalWidth, maxH / im.naturalHeight);
        const dw = im.naturalWidth * ratio, dh = im.naturalHeight * ratio;
        ctx.drawImage(im, cx - dw / 2, cy - dh / 2, dw, dh);
      } else {
        // No logo yet → a neutral "SPONSOR" placeholder so the surface still reads.
        ctx.font = "bold 11px 'DM Sans', sans-serif"; ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
        ctx.fillStyle = toRgba(lighten(P.accent, 0.4), 0.7);
        ctx.fillText('SPONSOR', cx, cy);
      }
    }
    // Stadium sponsor surfaces, drawn in the stands ABOVE the crossbar (y < GOAL_Y).
    function drawBackdrop(ctx: CanvasRenderingContext2D) {
      const showJumbo = backdrop === 'jumbotron' || backdrop === 'both';
      const showBoard = backdrop === 'billboard' || backdrop === 'both';
      const screenFill = darken(brand.primary, 0.2);

      if (showBoard) {
        // Wide LED perimeter board spanning behind the goal.
        const bw = backdrop === 'both' ? 250 : 268, bh = backdrop === 'both' ? 26 : 32;
        const bx = CX - bw / 2, by = backdrop === 'both' ? 60 : 50;
        ctx.save();
        ctx.shadowColor = toRgba(P.glow, 0.5); ctx.shadowBlur = 12;
        roundRect(ctx, bx, by, bw, bh, 4); ctx.fillStyle = screenFill; ctx.fill();
        ctx.restore();
        roundRect(ctx, bx, by, bw, bh, 4); ctx.lineWidth = 1.5; ctx.strokeStyle = toRgba(P.glow, 0.7); ctx.stroke();
        // faint LED scanlines
        ctx.save(); roundRect(ctx, bx, by, bw, bh, 4); ctx.clip();
        ctx.strokeStyle = 'rgba(255,255,255,.04)'; ctx.lineWidth = 1;
        for (let y = by + 2; y < by + bh; y += 3) { ctx.beginPath(); ctx.moveTo(bx, y); ctx.lineTo(bx + bw, y); ctx.stroke(); }
        ctx.restore();
        // support posts
        ctx.strokeStyle = toRgba(brand.secondary, 0.35); ctx.lineWidth = 2;
        ctx.beginPath(); ctx.moveTo(bx + 24, by + bh); ctx.lineTo(bx + 24, GOAL_Y - 2);
        ctx.moveTo(bx + bw - 24, by + bh); ctx.lineTo(bx + bw - 24, GOAL_Y - 2); ctx.stroke();
        drawLogoFitted(ctx, CX, by + bh / 2, bw - 28, bh - 10);
      }

      if (showJumbo) {
        // Big-screen jumbotron, centered above the goal.
        const pw = 150, ph = 50, px0 = CX - pw / 2, py0 = backdrop === 'both' ? 14 : 18;
        // support legs down toward the crossbar
        ctx.strokeStyle = toRgba(brand.secondary, 0.4); ctx.lineWidth = 3;
        ctx.beginPath(); ctx.moveTo(CX - 34, py0 + ph); ctx.lineTo(CX - 34, GOAL_Y - 4);
        ctx.moveTo(CX + 34, py0 + ph); ctx.lineTo(CX + 34, GOAL_Y - 4); ctx.stroke();
        // screen + glow
        ctx.save();
        ctx.shadowColor = toRgba(P.glow, 0.8); ctx.shadowBlur = 18;
        roundRect(ctx, px0, py0, pw, ph, 8); ctx.fillStyle = screenFill; ctx.fill();
        ctx.restore();
        roundRect(ctx, px0, py0, pw, ph, 8); ctx.lineWidth = 2; ctx.strokeStyle = toRgba(P.glow, 0.75); ctx.stroke();
        roundRect(ctx, px0 + 5, py0 + 5, pw - 10, ph - 10, 5); ctx.fillStyle = 'rgba(0,0,0,.32)'; ctx.fill();
        drawLogoFitted(ctx, CX, py0 + ph / 2, pw - 24, ph - 22);
      }
    }
    function drawGoalNet(ctx: CanvasRenderingContext2D, gx: number) {
      const l = gx - GW / 2, r = gx + GW / 2, t = GOAL_Y, b = GOAL_Y + GH;
      ctx.save();
      ctx.beginPath(); ctx.rect(l, t, GW, GH); ctx.clip();
      ctx.fillStyle = P.netBack; ctx.fillRect(l, t, GW, GH);
      ctx.strokeStyle = P.netLine; ctx.lineWidth = 1;
      for (let i = 0; i <= 12; i++) { const x = l + (GW / 12) * i; ctx.beginPath(); ctx.moveTo(x, t); ctx.lineTo(x, b); ctx.stroke(); }
      for (let j = 0; j <= 7; j++) { const y = t + (GH / 7) * j; ctx.beginPath(); ctx.moveTo(l, y); ctx.lineTo(r, y); ctx.stroke(); }
      ctx.restore();
    }
    function drawPockets(ctx: CanvasRenderingContext2D, gx: number) {
      POCKETS.forEach((p, idx) => {
        const cx = gx + p.dx, cy = GOAL_Y + p.dy;
        const x = cx - p.w / 2, y = cy - p.h / 2;
        const seg = pockets[idx];
        const lit = fx.current.pocket === idx && fx.current.flash > 0;
        // pocket tint from brand accent, brighter for the winning/lit pocket
        const tint = idx === targetIdx ? lighten(P.accent, 0.2) : P.accent;
        roundRect(ctx, x, y, p.w, p.h, 7);
        ctx.fillStyle = toRgba(tint, lit ? 0.55 : 0.2); ctx.fill();
        ctx.lineWidth = lit ? 2.4 : 1.4;
        ctx.strokeStyle = toRgba(tint, lit ? 1 : 0.6); ctx.stroke();
        // prize emoji + stars
        const stars = Math.max(0, Math.min(5, (seg as any)?.stars ?? seg?.rating ?? 0));
        ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
        if (seg?.emoji) { ctx.font = '13px serif'; ctx.fillText(seg.emoji, cx, cy - 3); }
        ctx.font = "7px 'DM Sans', sans-serif"; ctx.fillStyle = toRgba(tint, 0.95);
        ctx.fillText('★'.repeat(stars), cx, cy + (seg?.emoji ? 9 : 0));
      });
    }
    function drawGoalFrame(ctx: CanvasRenderingContext2D, gx: number) {
      const l = gx - GW / 2, r = gx + GW / 2, t = GOAL_Y, b = GOAL_Y + GH;
      ctx.lineCap = 'round'; ctx.lineJoin = 'round';
      ctx.strokeStyle = P.glow; ctx.lineWidth = 9; ctx.globalAlpha = 0.25;
      ctx.strokeRect(l, t, GW, GH); ctx.globalAlpha = 1;
      ctx.strokeStyle = '#F5F3FF'; ctx.lineWidth = 6;
      ctx.beginPath(); ctx.moveTo(l, b); ctx.lineTo(l, t); ctx.lineTo(r, t); ctx.lineTo(r, b); ctx.stroke();
      ctx.strokeStyle = toRgba(lighten(P.glow, 0.5), 0.6); ctx.lineWidth = 1.5;
      ctx.beginPath(); ctx.moveTo(l + 3, t + 3); ctx.lineTo(r - 3, t + 3); ctx.stroke();
    }
    function drawKeeper(ctx: CanvasRenderingContext2D, gx: number) {
      const k = keeper.current;
      const baseX = gx, baseY = GOAL_Y + GH - 4;
      const diveX = k.dir * k.dive * 58;
      const lean = k.dir * k.dive * 0.5;
      const cx = baseX + diveX;
      const bob = phaseRef.current === 'ready' ? Math.sin(goalPhase.current * 2.2) * 1.6 : 0;
      ctx.save();
      ctx.translate(cx, baseY + bob);
      ctx.rotate(lean);
      ctx.strokeStyle = P.jersey; ctx.lineWidth = 4.5; ctx.lineCap = 'round';
      const armUp = k.dive * 0.9;
      ctx.beginPath(); ctx.moveTo(-3, -22); ctx.lineTo(-12, -22 - armUp * 14); ctx.stroke();
      ctx.beginPath(); ctx.moveTo(3, -22); ctx.lineTo(12, -22 - armUp * 14); ctx.stroke();
      ctx.fillStyle = P.gloves;
      ctx.beginPath(); ctx.arc(-12, -22 - armUp * 14, 3.4, 0, Math.PI * 2); ctx.fill();
      ctx.beginPath(); ctx.arc(12, -22 - armUp * 14, 3.4, 0, Math.PI * 2); ctx.fill();
      roundRect(ctx, -7, -26, 14, 22, 5); ctx.fillStyle = P.jersey; ctx.fill();
      // subtle rim so the keeper reads even when the kit matches the net/field (e.g. Modelo navy)
      ctx.lineWidth = 1.2; ctx.strokeStyle = 'rgba(255,255,255,.28)'; ctx.stroke();
      ctx.strokeStyle = darken(P.jersey, 0.5); ctx.lineWidth = 4;
      ctx.beginPath(); ctx.moveTo(-3, -5); ctx.lineTo(-5, 4); ctx.stroke();
      ctx.beginPath(); ctx.moveTo(3, -5); ctx.lineTo(5, 4); ctx.stroke();
      ctx.beginPath(); ctx.arc(0, -31, 5.2, 0, Math.PI * 2); ctx.fillStyle = '#E9C9A8'; ctx.fill();
      ctx.restore();
    }
  }, [P, aimVector, backdrop, brand, demoPhase, fgMuted, fgSubtle, goalX, hint, language, pocketPos, pockets, targetIdx]);

  // ── Set up demo frames (static, inspectable states) ──
  useEffect(() => {
    revealedFired.current = false;
    trail.current = [];
    fx.current = { ripple: 0, pocket: -1, flash: 0, shake: 0 };
    keeper.current = { dive: 0, dir: 0 };
    if (!demoPhase) {
      phaseRef.current = 'ready';
      goalSlow.current = 1;
      ballPos.current = { x: SPOT.x, y: SPOT.y, r: 30 };
      setHint(true);
      return;
    }
    setHint(false);
    phaseRef.current = demoPhase;
    const end = pocketPos(targetIdx, CX);
    if (demoPhase === 'ready') {
      goalSlow.current = 1; ballPos.current = { x: SPOT.x, y: SPOT.y, r: 30 };
    } else if (demoPhase === 'aiming') {
      goalSlow.current = 0; ballPos.current = { x: SPOT.x, y: SPOT.y, r: 30 };
    } else if (demoPhase === 'flight') {
      goalSlow.current = 0;
      const tt = 0.6;
      const C = { x: SPOT.x + 0.16 * 200, y: SPOT.y - 0.99 * 200 - 0.62 * 70 };
      flight.current = { t: tt, S: { x: SPOT.x, y: SPOT.y }, C };
      const mt = 1 - tt;
      ballPos.current = {
        x: mt * mt * SPOT.x + 2 * mt * tt * C.x + tt * tt * end.x,
        y: mt * mt * SPOT.y + 2 * mt * tt * C.y + tt * tt * end.y,
        r: lerp(30, 9, Math.pow(tt, 0.85)),
      };
      keeper.current = { dive: clamp((tt - 0.42) / 0.4, 0, 1), dir: -1 };
      trail.current = [0.3, 0.42, 0.54].map((t) => {
        const m = 1 - t;
        return { x: m * m * SPOT.x + 2 * m * t * C.x + t * t * end.x, y: m * m * SPOT.y + 2 * m * t * C.y + t * t * end.y, r: lerp(30, 9, t) };
      });
    } else if (demoPhase === 'scored' || demoPhase === 'winReveal') {
      goalSlow.current = 0;
      ballPos.current = { x: end.x, y: end.y, r: 11 };
      fx.current = { ripple: demoPhase === 'scored' ? 1 : 0.4, pocket: targetIdx, flash: 1, shake: 0 };
      keeper.current = { dive: 1, dir: -1 };
    }
    force((n) => n + 1);
  }, [demoPhase, pocketPos, targetIdx]);

  // ── Animation loop ──
  useEffect(() => {
    let id: number;
    const loop = () => {
      const phase = phaseRef.current;
      goalPhase.current += 0.02 * goalSlow.current;
      if (fx.current.flash > 0) fx.current.flash = Math.max(0, fx.current.flash - 0.04);
      if (fx.current.ripple > 0 && !demoPhase) fx.current.ripple = Math.max(0, fx.current.ripple - 0.03);
      if (fx.current.shake > 0) fx.current.shake = Math.max(0, fx.current.shake - 0.6);

      if (!demoPhase) {
        if (phase === 'ready') {
          ballRot.current.y += 0.01; ballRot.current.x += 0.002;
          ballPos.current = { x: SPOT.x, y: SPOT.y, r: 30 };
        } else if (phase === 'flight' && flight.current) {
          const fl = flight.current;
          fl.t = Math.min(1, fl.t + 1 / FLIGHT_FRAMES);
          const t = fl.t;
          const gx = goalX();
          const end = pocketPos(targetIdx, gx);
          const mt = 1 - t;
          const bx = mt * mt * fl.S.x + 2 * mt * t * fl.C.x + t * t * end.x;
          const by = mt * mt * fl.S.y + 2 * mt * t * fl.C.y + t * t * end.y;
          const r = lerp(30, 9, Math.pow(t, 0.85));
          ballPos.current = { x: bx, y: by, r };
          ballRot.current.y += 0.34; ballRot.current.x += 0.12;
          trail.current.push({ x: bx, y: by, r }); if (trail.current.length > 9) trail.current.shift();
          keeper.current.dive = clamp((t - 0.42) / 0.4, 0, 1);
          if (t >= 1) {
            phaseRef.current = 'scored';
            fx.current.ripple = 1; fx.current.flash = 1; fx.current.pocket = targetIdx; fx.current.shake = 4;
            trail.current = [];
            window.setTimeout(() => {
              if (revealedFired.current) return;
              revealedFired.current = true;
              phaseRef.current = 'winReveal';
              onRevealed?.(prize);
            }, 900);
          }
        }
      } else if (demoPhase === 'ready') {
        ballRot.current.y += 0.01; ballRot.current.x += 0.002;
      }
      draw();
      id = requestAnimationFrame(loop);
    };
    id = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(id);
  }, [draw, demoPhase, goalX, pocketPos, targetIdx, onRevealed, prize]);

  // ── Pointer (slingshot) ──
  const getPos = (e: React.PointerEvent, cv: HTMLCanvasElement) => {
    const r = cv.getBoundingClientRect();
    return { x: (e.clientX - r.left) * (CW / r.width), y: (e.clientY - r.top) * (CH / r.height) };
  };
  const onDown = (e: React.PointerEvent) => {
    if (demoPhase || phaseRef.current !== 'ready') return;
    const cv = cvRef.current!; const { x, y } = getPos(e, cv);
    drag.current = { active: true, sx: x, sy: y, cx: x, cy: y };
    cv.setPointerCapture(e.pointerId);
    setHint(false);
  };
  const onMove = (e: React.PointerEvent) => {
    if (!drag.current.active) return;
    const { x, y } = getPos(e, cvRef.current!);
    drag.current.cx = x; drag.current.cy = y;
  };
  const onUp = () => {
    if (!drag.current.active) return;
    drag.current.active = false;
    const v = aimVector();
    if (v && v.power > 0.14) launch(v);
  };

  const prizeName = language === 'es' ? (prize?.nameTranslate || prize?.name) : prize?.name;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12 }}>
      <canvas
        ref={cvRef}
        width={CW}
        height={CH}
        role="img"
        aria-label={
          phaseRef.current === 'winReveal' || phaseRef.current === 'scored'
            ? `Goal — you won ${prizeName}`
            : `${T.hint[language]}. ${T.sub[language]}`
        }
        onPointerDown={onDown}
        onPointerMove={onMove}
        onPointerUp={onUp}
        onPointerLeave={onUp}
        style={{
          display: 'block', width: '100%', maxWidth: CW, borderRadius: 18,
          touchAction: 'none', cursor: demoPhase ? 'default' : 'grab',
          WebkitTapHighlightColor: 'transparent', userSelect: 'none',
          boxShadow: `0 12px 40px ${toRgba(darken(brand.primary, 0.4), 0.5)}`,
        }}
      />

      {/* Non-gesture fallback (accessibility): shoot without a flick */}
      {!demoPhase && (
        <button
          type="button"
          onClick={() => launch({ power: 0.62, fx: 0.12, fy: -0.99 })}
          style={{
            border: `1.5px solid ${brand.accent}`, color: brand.accent, background: 'transparent',
            borderRadius: 999, padding: '8px 22px', fontSize: 14, fontWeight: 700, cursor: 'pointer',
            fontFamily: "'DM Sans', sans-serif",
          }}
        >
          ⚽ {T.shoot[language]}
        </button>
      )}

      {/* Powered by Xotski — 8px, bottom-center, non-configurable */}
      <div aria-hidden style={{ fontSize: 8, letterSpacing: 0.5, color: lightBg ? 'rgba(0,0,0,0.55)' : fgMuted }}>
        Powered by Xotski
      </div>
    </div>
  );
};

export default SoccerBall;
